import { Icon } from './chunk-VH4ICUUK.js';
import { tv } from './chunk-7NOFBOMW.js';
import { cn } from './chunk-UYYBXMNI.js';
import * as SelectPrimitive from '@radix-ui/react-select';
import { forwardRef } from 'react';
import { jsxs, jsx } from 'react/jsx-runtime';

var Select = ({ children, ...props }) => /* @__PURE__ */ jsx(SelectPrimitive.Root, { ...props, children });
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var selectTriggerStyles = tv({
  base: [
    "border-input ring-offset-background placeholder:text-muted-foreground hover:bg-muted flex h-9 w-full items-center justify-between whitespace-nowrap rounded-lg border bg-transparent px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
    "focus-visible:ring-ring focus:ring-offset-2 focus-visible:outline-none focus-visible:ring-2"
  ],
  variants: {
    size: {
      default: "h-10",
      sm: "h-9"
    },
    invalid: {
      true: "border-destructive"
    }
  }
});
var SelectTrigger = forwardRef(
  ({ className, children, invalid, size = "default", ...props }, ref) => /* @__PURE__ */ jsxs(SelectPrimitive.Trigger, { ref, className: selectTriggerStyles({ className, size, invalid }), ...props, children: [
    children,
    /* @__PURE__ */ jsx(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx(Icon, { name: "chevron-down", className: "h-4 w-4 opacity-50" }) })
  ] })
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var selectScrollUpButtonStyles = tv({
  base: "flex cursor-default items-center justify-center py-1"
});
var SelectScrollUpButton = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.ScrollUpButton, { ref, className: selectScrollUpButtonStyles({ className }), ...props, children: /* @__PURE__ */ jsx(Icon, { name: "chevron-up" }) }));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var selectScrollDownButtonStyles = tv({
  base: "flex cursor-default items-center justify-center py-1"
});
var SelectScrollDownButton = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.ScrollDownButton, { ref, className: selectScrollDownButtonStyles({ className }), ...props, children: /* @__PURE__ */ jsx(Icon, { name: "chevron-down" }) }));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var selectContentStyles = tv({
  slots: {
    base: [
      "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-lg border shadow-md",
      "focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-transparent"
    ],
    viewport: [
      "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
      "p-1",
      "focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-transparent"
    ]
  },
  variants: {
    position: {
      ["item-aligned"]: {
        base: "",
        viewport: "p-1"
      },
      popper: {
        base: "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        viewport: "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
      }
    }
  },
  defaultVariants: {
    position: "popper"
  }
});
var SelectContent = forwardRef(
  ({ className, classNames, children, position = "popper", ...props }, ref) => {
    const slots = selectContentStyles();
    const baseClasses = cn(classNames?.base, className);
    const viewportClasses = classNames?.viewport;
    return /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
      SelectPrimitive.Content,
      {
        ref,
        className: slots.base({ class: baseClasses, position }),
        position,
        ...props,
        children: [
          /* @__PURE__ */ jsx(SelectScrollUpButton, {}),
          /* @__PURE__ */ jsx(SelectPrimitive.Viewport, { className: slots.viewport({ class: viewportClasses, position }), children }),
          /* @__PURE__ */ jsx(SelectScrollDownButton, {})
        ]
      }
    ) });
  }
);
SelectContent.displayName = SelectPrimitive.Content.displayName;
var selectLabelStyles = tv({
  base: "ml-6 px-2 py-1.5 pl-1.5 text-sm font-semibold"
});
var SelectLabel = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Label, { ref, className: selectLabelStyles({ className }), ...props })
);
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var selectItemStyles = tv({
  base: [
    "focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default select-none items-center rounded-md py-1.5 pl-2 pr-2 text-sm",
    "gap-1.5 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    "focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-transparent"
  ]
});
var SelectItem = forwardRef(
  ({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(SelectPrimitive.Item, { ref, className: selectItemStyles({ className }), ...props, children: [
    /* @__PURE__ */ jsx("span", { className: "flex h-4 w-4 items-center justify-center", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Icon, { name: "check", className: "h-4 w-4" }) }) }),
    /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
  ] })
);
SelectItem.displayName = SelectPrimitive.Item.displayName;
var selectSeparatorStyles = tv({
  base: "bg-muted -mx-1 my-1 h-px"
});
var SelectSeparator = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Separator, { ref, className: selectSeparatorStyles({ className }), ...props })
);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, selectContentStyles, selectItemStyles, selectScrollDownButtonStyles, selectScrollUpButtonStyles, selectSeparatorStyles, selectTriggerStyles };
//# sourceMappingURL=chunk-MSAUO5RU.js.map
//# sourceMappingURL=chunk-MSAUO5RU.js.map